import React, { useState, useEffect, ReactNode } from 'react'
import { IGif } from '@giphy/js-types'
import Grid from '@giphy/react-giphy-grid'
import { Loader } from 'shared/components/preloader-animation'
import { getArtistChannels, request } from 'shared/api'
import getGifSkeleton from 'shared/components/gif-skeleton'
import NoResults from './no-results'

type Props = {
    filter: string
    columns?: number
    gutter?: number
    isFreelance?: boolean
    sort: string
    children: ReactNode
}

const ArtistsGrid = ({ filter = '', isFreelance = false, sort, children, columns, gutter }: Props) => {
    const [gifs, setGifs] = useState<IGif[]>([])
    const [nextUrl, setNextUrl] = useState<string>('')
    const [isFetching, setIsFetching] = useState<boolean>(false)
    const [isDone, setIsDone] = useState<boolean>(false)
    useEffect(() => {
        setGifs([])
        fetchArtists('', true)
    }, [filter, isFreelance, sort])

    const fetchArtists = async (url = '', abandonCurrentFetch = false) => {
        if ((isFetching || isDone) && !abandonCurrentFetch) return
        setIsFetching(true)
        const { results = [], next = '' } = url
            ? await request(url, {}) // typescript expects a second 'config' arg here, passing an empty object reoslves it
            : await getArtistChannels({
                  filter: filter,
                  freelance: isFreelance,
                  order: sort === 'updatetime' ? 'desc' : 'asc',
                  sort: sort,
                  rating: 'pg-13',
              })
        // filter out any channels that do not have a featured_gif set, as these will break
        const newGifs = results.map(({ featured_gif, user }) => {
            let gif = featured_gif
            // if there is no featured gif set or the gif is private,
            // fall back to an empty GIF. we need to make sure the
            // artist is not hidden from this page.
            if (!gif || gif.is_hidden || gif.is_removed) {
                gif = getGifSkeleton()
            }
            return { ...gif, url: user.profile_url, user }
        })

        setGifs(abandonCurrentFetch ? [...newGifs] : [...gifs, ...newGifs])
        setNextUrl(next)
        setIsFetching(false)
        setIsDone(!next)
    }

    const fetchMoreArtists = () => {
        nextUrl && fetchArtists(nextUrl)
    }
    return (
        <>
            {!gifs.length && !isFetching ? (
                <NoResults />
            ) : (
                !!gifs.length && (
                    <Grid
                        gifs={gifs}
                        autoPlay={true}
                        columns={columns || 3}
                        gutter={gutter || 16}
                        overlayElement={children}
                        showTags={false}
                        onLoadMore={fetchMoreArtists}
                    />
                )
            )}
            <Loader isFetching={isFetching} />
        </>
    )
}

export default ArtistsGrid
